/**
 * Client-side application configuration.
 *
 * Centralized place for app-level constants. No hardcoded values scattered across components.
 * Values can be overridden via Vite env vars (VITE_APP_*).
 */
export const appConfig = {
  /** App name shown in UI, page titles, etc. */
  name: import.meta.env.VITE_APP_NAME || 'Rocket Routine OS',

  /** localStorage key prefix — prevents collisions between apps on same domain */
  storagePrefix: import.meta.env.VITE_APP_STORAGE_PREFIX || 'saas',

  /** Support email */
  supportEmail: import.meta.env.VITE_SUPPORT_EMAIL || 'support@example.com',

  /**
   * Tenant base domain for subdomain tenancy (AC-PROD-1, AC-PROD-4).
   * Set VITE_TENANT_BASE_DOMAIN=roros.ai in production .env.
   * When set, the frontend detects tenant/apex mode from window.location.hostname.
   */
  tenantBaseDomain: import.meta.env.VITE_TENANT_BASE_DOMAIN || '',

  features: {
    registration: (import.meta.env.VITE_ENABLE_REGISTRATION ?? 'true') !== 'false',
    aiGeneration: import.meta.env.VITE_ENABLE_AI_GENERATION === 'true',
    fileUploads: (import.meta.env.VITE_ENABLE_FILE_UPLOADS ?? 'true') !== 'false',
    knowledgeBase: (import.meta.env.VITE_ENABLE_KNOWLEDGE_BASE ?? 'true') !== 'false',
  },
} as const;

/**
 * Landing route for non-super-admin users after login and on reload.
 *
 * Special case: when set to `'/'`, the root router loader in main.tsx
 * renders the protected app **inline** instead of redirecting. Any other
 * value triggers a redirect from `'/'` to `HOME_ROUTE`. See the root
 * loader in main.tsx for the branch.
 *
 * Configurable via `VITE_HOME_ROUTE`. Defaults to `'/organization'` —
 * a settings-flavored org overview that's a safe neutral default for
 * the boilerplate. Most product installs will override this via env
 * var to point at their actual product surface.
 */
export const HOME_ROUTE = import.meta.env.VITE_HOME_ROUTE || '/organization';

/** Helper: get a prefixed localStorage key */
export function storageKey(key: string): string {
  return `${appConfig.storagePrefix}_${key}`;
}
